import { reportHandledException } from '@/lib/error-reporting'
import { useEffect, useRef, useState } from 'react'
import { subscribeActiveContext } from '@kiao-voice/api-client'
import type { preflightAudioBatch } from './api-client'
import type { AudioBatchPreflightResponse } from './types'

export interface SelectionPreflightOptions {
  open: boolean
  queue: File[]
  visualContext: boolean
  maxFileMinutes: number | null
  currentPlan: string | null
  loadStoredToken: () => string | null
  probeDuration: (file: File) => Promise<number | null>
  preflight: typeof preflightAudioBatch | null
}
export type SelectionPreflightWarning =
  | {
      kind: 'credits'
      estimate: Extract<AudioBatchPreflightResponse, { reason: 'insufficient_credits' }>
    }
  | {
      kind: 'duration'
      fileName: string
      durationSecs: number
      maxFileMinutes: number
      plan: string | null
    }

/** Advisory selection feedback only; Start always performs its own fresh check. */
export function useUploadSelectionPreflight({
  open,
  queue,
  visualContext,
  maxFileMinutes,
  currentPlan,
  loadStoredToken,
  probeDuration,
  preflight,
}: SelectionPreflightOptions): SelectionPreflightWarning | null {
  const durations = useRef(new WeakMap<File, Promise<number | null>>())
  const [view, setView] = useState<{
    queue: File[]
    visualContext: boolean
    maxFileMinutes: number | null
    currentPlan: string | null
    warning: SelectionPreflightWarning
  } | null>(null)

  useEffect(() => {
    setView(null)
    if (
      !open ||
      queue.length === 0 ||
      (!preflight && maxFileMinutes === null) ||
      !loadStoredToken()
    )
      return
    let controller: AbortController | null = null
    let generation = 0
    const invalidate = () => {
      generation += 1
      controller?.abort()
      setView(null)
    }
    const run = async () => {
      invalidate()
      const mine = generation
      const abort = new AbortController()
      controller = abort
      const isCurrent = () => generation === mine && !abort.signal.aborted
      const show = (warning: SelectionPreflightWarning) => {
        if (isCurrent()) setView({ queue, visualContext, maxFileMinutes, currentPlan, warning })
      }
      try {
        const files = await Promise.all(
          queue.map(async (file) => {
            let duration = durations.current.get(file)
            if (!duration) {
              duration = Promise.resolve()
                .then(() => probeDuration(file))
                .then(
                  (value) => (value !== null && Number.isFinite(value) && value > 0 ? value : null),
                  () => {
                    durations.current.delete(file)
                    return null
                  }
                )
              durations.current.set(file, duration)
            }
            const durationSecs = await duration
            return { durationSecs }
          })
        )
        if (!isCurrent()) return
        const overCap = files.findIndex(
          ({ durationSecs }) =>
            durationSecs !== null && maxFileMinutes !== null && durationSecs > maxFileMinutes * 60
        )
        if (overCap >= 0 && maxFileMinutes !== null) {
          show({
            kind: 'duration',
            fileName: queue[overCap].name,
            durationSecs: files[overCap].durationSecs!,
            maxFileMinutes,
            plan: currentPlan,
          })
          return
        }
        if (
          !preflight ||
          !files.some(({ durationSecs }) => durationSecs !== null) ||
          !loadStoredToken()
        )
          return
        const response = await preflight({ files, visualContext }, { signal: abort.signal })
        if (!isCurrent() || response.ok) return
        if (response.reason === 'insufficient_credits')
          show({ kind: 'credits', estimate: response })
        else
          show({
            kind: 'duration',
            fileName: queue[response.fileIndex]?.name ?? '',
            durationSecs: response.durationSecs,
            maxFileMinutes: response.maxFileMinutes,
            plan: response.plan,
          })
      } catch (caughtError) {
        reportHandledException(caughtError, {
          source: 'features/batch-transcription/use-upload-selection-preflight.ts',
        })
        // Advisory failure never submits or grants permission. Start retains its own auth/error policy.
      }
    }
    void run()
    const unsubscribe = subscribeActiveContext(() => {
      void run()
    })
    return () => {
      invalidate()
      unsubscribe()
    }
  }, [
    open,
    queue,
    visualContext,
    maxFileMinutes,
    currentPlan,
    loadStoredToken,
    probeDuration,
    preflight,
  ])

  return open &&
    view?.queue === queue &&
    view.visualContext === visualContext &&
    view.maxFileMinutes === maxFileMinutes &&
    view.currentPlan === currentPlan
    ? view.warning
    : null
}
